import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { listShots, type ScriptScene } from '@/api/studio-production'
import { SceneReferenceAssets } from '@/features/studio/components/SceneReferenceAssets'
import { buildShotSceneScopeKey } from '@/features/studio/lib/canvas-scope'
import { fetchProductionScriptEpisode } from '@/features/studio/lib/script-episode-cache'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'

export function EpisodeScenesExpandRows({
  productionId,
  scriptId,
  episodeNo,
  canEdit,
  onPreview,
}: {
  productionId: string
  scriptId: number
  episodeNo: number
  canEdit: boolean
  onPreview?: (urls: string[], index: number, title: string) => void
}) {
  const { t } = useTranslation()
  const [scenes, setScenes] = useState<ScriptScene[]>([])
  const [shotCountByScene, setShotCountByScene] = useState<Record<number, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    if (!episodeNo || !scriptId) return
    setLoading(true)
    setError(null)
    try {
      const [scriptRes, shotRes] = await Promise.all([
        fetchProductionScriptEpisode(productionId, scriptId, episodeNo),
        listShots(productionId, {
          script_id: scriptId,
          episode_no: episodeNo,
          limit: 500,
        }).catch(() => ({ items: [], total: 0 })),
      ])
      setScenes(scriptRes.scenes ?? [])
      const counts: Record<number, number> = {}
      for (const shot of shotRes.items) {
        counts[shot.scene_no] = (counts[shot.scene_no] ?? 0) + 1
      }
      setShotCountByScene(counts)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, scriptId, episodeNo])

  useEffect(() => {
    void reload()
  }, [reload])

  function updateSceneStoryboard(sceneNo: number, url: string) {
    setScenes((prev) =>
      prev.map((sc) => (sc.scene_no === sceneNo ? { ...sc, storyboard_url: url } : sc)),
    )
  }

  if (loading) {
    return (
      <tr className="border-b border-white/[0.04] bg-white/[0.01]">
        <td colSpan={6} className="px-4 py-4 pl-12">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
            {t('shots.expandingScenes')}
          </div>
        </td>
      </tr>
    )
  }

  if (error) {
    return (
      <tr className="border-b border-white/[0.04] bg-white/[0.01]">
        <td colSpan={6} className="px-4 py-3 pl-12 text-xs text-rose-300">
          {t(error, { defaultValue: error })}
        </td>
      </tr>
    )
  }

  if (scenes.length === 0) {
    return (
      <tr className="border-b border-white/[0.04] bg-white/[0.01]">
        <td colSpan={6} className="px-4 py-3 pl-12 text-xs text-zinc-600">
          {t('shots.noScenes')}
        </td>
      </tr>
    )
  }

  return (
    <>
      {scenes.map((sc) => {
        const scopeKey = buildShotSceneScopeKey(scriptId, episodeNo, sc.scene_no)
        const sceneCode = `S${String(sc.scene_no).padStart(2, '0')}`
        return (
          <tr key={sc.scene_no} className="border-b border-white/[0.04] bg-white/[0.01] align-top">
            <td colSpan={2} className="px-4 py-3 pl-12">
              <p className="text-sm text-zinc-300">
                <span className="font-mono text-xs text-indigo-300">{sceneCode}</span>
                {sc.heading?.trim() ? (
                  <span className="ml-2 text-zinc-400">{sc.heading.trim()}</span>
                ) : null}
              </p>
            </td>
            <td className="px-4 py-3 text-xs text-zinc-600">—</td>
            <td className="px-4 py-3 text-xs text-zinc-500">
              {t('shots.sceneShotCount', { count: shotCountByScene[sc.scene_no] ?? 0 })}
            </td>
            <td colSpan={2} className="px-4 py-3">
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-zinc-600">
                {t('shots.sceneReferences')}
              </p>
              <SceneReferenceAssets
                productionId={productionId}
                scriptId={scriptId}
                episodeNo={episodeNo}
                sceneNo={sc.scene_no}
                scopeKey={scopeKey}
                storyboardUrl={sc.storyboard_url}
                canEdit={canEdit}
                compact
                onStoryboardChange={(url) => updateSceneStoryboard(sc.scene_no, url)}
                onPreview={onPreview}
              />
            </td>
          </tr>
        )
      })}
    </>
  )
}
